/**
 * First-party frontend performance collector (EEL98).
 *
 * Measures what the person in front of the browser actually got — Core Web Vitals, the
 * Navigation Timing breakdown, what the page had to download, and how long each Livewire
 * round trip took — and beacons it once, at page hide, to our own ingest endpoint.
 *
 * It runs on every page we serve, so it inherits the error collector's constraints and adds
 * one of its own:
 *
 *   - **It must never break a page.** Every observer is registered inside its own try/catch,
 *     the flush is wrapped, and a missing browser API costs that metric and nothing else.
 *   - **It must cost nothing measurable.** The sampling coin flip happens *first*: an
 *     unsampled page view registers no observer, allocates no buffer, and reads no timing
 *     entry. A sampled one pays the browser's own observer callbacks (which it is already
 *     computing for its own dev tools) plus exactly one `sendBeacon` after the page is
 *     hidden, where latency is no longer user-visible.
 *   - **It must not become its own subject.** Nothing here reports errors, so a fault in this
 *     module cannot generate telemetry about itself.
 *
 * **On INP.** This reports the *worst* interaction latency rather than the 98th percentile of
 * interactions. For any page view with fewer than ~50 interactions those are the same number
 * by definition, which covers the overwhelming majority of real page views; past that, this
 * over-reports slightly (it keeps the single worst interaction rather than discarding the top
 * 2%). That is a deliberate trade for staying dependency-free — see the task plan's rejection
 * of the `web-vitals` package — and it errs toward flagging a bad experience rather than
 * hiding one.
 *
 * Correlation to the backend is the `Server-Timing: wof;desc="<trace_id>"` header
 * TracePerformance emits. It is the only response header a browser hands back to JavaScript,
 * which is why it, rather than an HTML island, carries the trace id.
 */

/** Server-Timing metric name TracePerformance uses. Must match its TRACE_METRIC constant. */
const TRACE_METRIC = 'wof'

/** Only interactions at least this long are worth observing (ms). Below it, nobody notices. */
const EVENT_DURATION_THRESHOLD = 40

/**
 * CLS session window, per the Core Web Vitals definition: shifts more than 1s apart, or more
 * than 5s from the window's start, begin a new window, and the score is the largest window.
 */
const CLS_SESSION_GAP_MS = 1000
const CLS_SESSION_MAX_MS = 5000

/** Initiator types we summarise. Anything else is folded into `other`. */
const RESOURCE_KINDS = ['script', 'css', 'image', 'font', 'fetch']

/** Matches the server's `round_trips.*.name` column width. */
const MAX_NAME_LENGTH = 191

let installed = false

/**
 * Install the performance collector. Returns without touching a single browser API when
 * this page view is not sampled, which is the common case by design.
 *
 * @param {{performance_enabled?: boolean, performance_endpoint?: string, performance_sample_rate?: number, token?: string, max_round_trips?: number}} config
 * @param {(payload: object) => void} [livewireComponentsFrom] parser shared with the error collector
 */
export function installPerformance(config, livewireComponentsFrom) {
    try {
        if (installed || !config || config.performance_enabled !== true || !config.performance_endpoint) {
            return
        }

        if (!sampled(config.performance_sample_rate)) {
            return
        }

        installed = true

        const state = {
            config,
            viewId: viewId(),
            occurredAt: new Date().toISOString(),
            traceId: navigationTraceId(),
            lcp: null,
            inp: null,
            cls: 0,
            clsWindow: { value: 0, first: 0, last: 0 },
            roundTrips: [],
            roundTripCount: 0,
            roundTripMsTotal: 0,
            flushed: false,
            dirty: true,
        }

        observeLcp(state)
        observeCls(state)
        observeInp(state)
        installLivewireTiming(state, livewireComponentsFrom)
        installUiTiming(state)
        installFlushTriggers(state)
    } catch (e) {
        // A collector that throws during install is strictly worse than no collector.
    }
}

/**
 * The sampling gate, and the reason it is the first thing that happens.
 *
 * The explicit bounds are load-bearing for the same reason DecidePerformanceCapture's are: a
 * bare `Math.random() < rate` comparison admits a page view at rate 0, which is precisely the
 * leak a "measure nothing" rate must not have.
 */
function sampled(rate) {
    const parsed = typeof rate === 'number' ? rate : Number(rate)

    if (!Number.isFinite(parsed) || parsed <= 0) {
        return false
    }

    if (parsed >= 1) {
        return true
    }

    return Math.random() < parsed
}

function viewId() {
    try {
        if (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function') {
            return globalThis.crypto.randomUUID()
        }
    } catch (e) {
        // Fall through: a page view id only has to be unique enough to make the ingest
        // upsert idempotent, so a timestamp plus randomness is an acceptable degradation.
    }

    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`
}

/**
 * The backend trace id for the document request, read off the navigation entry's exposed
 * Server-Timing metrics. Null on a browser that does not expose them, and on a page served
 * from a shared cache (where the server deliberately withheld the header).
 */
function navigationTraceId() {
    return traceIdFromServerTiming(navigationEntry()?.serverTiming)
}

function traceIdFromServerTiming(serverTiming) {
    if (!Array.isArray(serverTiming)) {
        return null
    }

    for (const metric of serverTiming) {
        if (metric && metric.name === TRACE_METRIC && typeof metric.description === 'string' && metric.description !== '') {
            return metric.description
        }
    }

    return null
}

/**
 * Parse a raw `Server-Timing` header value, for the Livewire path where the collector holds
 * the `Response` itself rather than a PerformanceResourceTiming entry.
 *
 * Only the shape we emit is recognised — `wof;desc="<id>"` — because this runs on every
 * round trip and a general-purpose header parser would be doing work for values nobody reads.
 */
function traceIdFromHeader(value) {
    if (typeof value !== 'string' || value === '') {
        return null
    }

    const match = new RegExp(`(?:^|,)\\s*${TRACE_METRIC}\\s*;\\s*desc\\s*=\\s*"?([^";,]+)"?`).exec(value)

    return match ? match[1] : null
}

function navigationEntry() {
    try {
        const entries = globalThis.performance?.getEntriesByType?.('navigation')

        return Array.isArray(entries) && entries.length > 0 ? entries[0] : null
    } catch (e) {
        return null
    }
}

/**
 * Register a PerformanceObserver, swallowing the failure when the browser does not know the
 * entry type. `buffered: true` is what recovers entries that fired before this module — a
 * deferred module always evaluates after FCP, and often after LCP.
 */
function observe(type, callback, options = {}) {
    try {
        const observer = new globalThis.PerformanceObserver(list => {
            try {
                callback(list.getEntries())
            } catch (e) {
                // One bad entry must not disable the observer for the rest of the page.
            }
        })

        observer.observe({ type, buffered: true, ...options })

        return observer
    } catch (e) {
        return null
    }
}

/**
 * LCP is reported repeatedly as bigger candidates render; the last one wins.
 *
 * Deliberately not frozen on first interaction the way the spec's "final" LCP is: the browser
 * itself stops emitting candidates after the first interaction, so taking the last entry we
 * were given produces the same value without this module having to track interactions to know
 * when to stop listening.
 */
function observeLcp(state) {
    observe('largest-contentful-paint', entries => {
        const last = entries[entries.length - 1]

        if (last && typeof last.startTime === 'number') {
            state.lcp = last.startTime
            state.dirty = true
        }
    })
}

/**
 * CLS as the maximum *session window* score, which is the actual Core Web Vitals definition —
 * a naive sum of every shift would make any long-lived page look broken.
 */
function observeCls(state) {
    observe('layout-shift', entries => {
        for (const entry of entries) {
            // A shift the user caused (by typing, clicking, resizing) is not a layout defect.
            if (entry.hadRecentInput) {
                continue
            }

            const window = state.clsWindow

            if (
                window.value !== 0 &&
                (entry.startTime - window.last > CLS_SESSION_GAP_MS ||
                    entry.startTime - window.first > CLS_SESSION_MAX_MS)
            ) {
                window.value = 0
                window.first = entry.startTime
            }

            if (window.value === 0) {
                window.first = entry.startTime
            }

            window.value += entry.value
            window.last = entry.startTime

            if (window.value > state.cls) {
                state.cls = window.value
                state.dirty = true
            }
        }
    })
}

/**
 * Interaction latency, grouped by `interactionId` so the several events of one interaction
 * (pointerdown, pointerup, click) report as the one interaction the user actually made.
 *
 * See the module docblock on why this is the worst interaction rather than a percentile.
 */
function observeInp(state) {
    observe(
        'event',
        entries => {
            for (const entry of entries) {
                // interactionId 0 means the event was not part of a discrete interaction.
                if (!entry.interactionId || typeof entry.duration !== 'number') {
                    continue
                }

                if (state.inp === null || entry.duration > state.inp) {
                    state.inp = entry.duration
                    state.dirty = true
                }
            }
        },
        { durationThreshold: EVENT_DURATION_THRESHOLD },
    )
}

/**
 * Time Livewire round trips as the user experiences them — request start to response, network
 * and all — and pair each with the backend transaction that served it.
 *
 * The backend already records its own duration for the same round trip; the gap between the
 * two is the network, which is the thing neither side can see alone.
 */
function installLivewireTiming(state, livewireComponentsFrom) {
    try {
        globalThis.document.addEventListener('livewire:init', () => {
            try {
                globalThis.Livewire.hook('request', ({ payload, respond, fail }) => {
                    const startedAt = now()
                    const startedAtIso = new Date().toISOString()
                    const name = componentName(payload, livewireComponentsFrom)

                    const record = (status, response) => {
                        recordRoundTrip(state, {
                            name,
                            kind: 'livewire',
                            duration_ms: Math.round(now() - startedAt),
                            status_code: typeof status === 'number' ? status : null,
                            trace_id: traceIdFromHeader(responseHeader(response, 'Server-Timing')),
                            occurred_at: startedAtIso,
                        })
                    }

                    // Both hooks, because a failed round trip is the one whose timing matters
                    // most and `respond` is not guaranteed to fire on every failure path.
                    if (typeof respond === 'function') {
                        respond(({ status, response }) => record(status, response))
                    }

                    if (typeof fail === 'function') {
                        fail(({ status, response }) => record(status, response))
                    }
                })
            } catch (e) {
                // Livewire's hook API changing shape must not take the rest of the collector
                // down with it.
            }
        })
    } catch (e) {
        // No document (a non-browser host) means nothing to hook.
    }
}

/**
 * Time Ui engine region round trips (EBI244). The engine runtime announces each applied region with a
 * `ui:timing` event, so this collector never imports it: request start to swapped, plus the swap alone.
 */
function installUiTiming(state) {
    try {
        globalThis.document.addEventListener('ui:timing', event => {
            try {
                const detail = event?.detail ?? {}
                const duration = integer(detail.duration_ms)

                recordRoundTrip(state, {
                    name: typeof detail.name === 'string' && detail.name !== '' ? detail.name.slice(0, MAX_NAME_LENGTH) : 'unknown',
                    kind: 'ui',
                    duration_ms: duration,
                    apply_ms: integer(detail.apply_ms),
                    status_code: typeof detail.status_code === 'number' ? detail.status_code : null,
                    trace_id: traceIdFromHeader(detail.server_timing ?? null),
                    // The event fires once the region is applied; the round trip began duration_ms earlier.
                    occurred_at: new Date(Date.now() - duration).toISOString(),
                })
            } catch (e) {
                // A malformed event costs that round trip, never the collector.
            }
        })
    } catch (e) {
        // No document (a non-browser host) means nothing to hook.
    }
}

function componentName(payload, livewireComponentsFrom) {
    if (typeof livewireComponentsFrom !== 'function') {
        return 'unknown'
    }

    try {
        const name = livewireComponentsFrom(payload)

        return typeof name === 'string' && name !== '' ? name.slice(0, MAX_NAME_LENGTH) : 'unknown'
    } catch (e) {
        return 'unknown'
    }
}

function responseHeader(response, header) {
    try {
        return response?.headers?.get?.(header) ?? null
    } catch (e) {
        return null
    }
}

/**
 * Buffer a round trip, bounded.
 *
 * `roundTripCount` keeps counting past the cap so the page view stays honest about how
 * interaction-heavy it was, while only the first N are shipped individually.
 */
function recordRoundTrip(state, roundTrip) {
    state.roundTripCount++
    state.roundTripMsTotal += roundTrip.duration_ms
    state.dirty = true

    const max = Number(state.config.max_round_trips)
    const cap = Number.isFinite(max) && max > 0 ? max : 20

    if (state.roundTrips.length < cap) {
        state.roundTrips.push(roundTrip)
    }
}

/**
 * Flush when the page is hidden — the only moment that reliably happens on every platform,
 * including an iOS tab the user swipes away, where `unload` never fires.
 *
 * A second flush is allowed if the tab was re-shown and something changed since, so a
 * long-lived tab's final CLS and later interactions are not lost. The server upserts on
 * `view_id`, which is what makes that safe rather than double-counting.
 */
function installFlushTriggers(state) {
    const flush = () => {
        try {
            if (!state.dirty) {
                return
            }

            send(state)
            state.flushed = true
            state.dirty = false
        } catch (e) {
            // A failed flush is not worth a second failure.
        }
    }

    try {
        globalThis.document.addEventListener('visibilitychange', () => {
            if (globalThis.document.visibilityState === 'hidden') {
                flush()
            }
        })

        // pagehide covers the bfcache path, where visibilitychange may not fire.
        globalThis.addEventListener('pagehide', flush)
    } catch (e) {
        // Nothing to attach to.
    }
}

function send(state) {
    const body = JSON.stringify(payload(state))
    const endpoint = state.config.performance_endpoint

    // sendBeacon first: the browser owns the request from here, so it survives the page going
    // away, which is exactly when this fires. It cannot set headers, which is why the token
    // travels in the body — the ingest gate accepts it there for this reason.
    try {
        if (typeof globalThis.navigator?.sendBeacon === 'function') {
            const blob = typeof globalThis.Blob === 'function'
                ? new globalThis.Blob([body], { type: 'application/json' })
                : body

            if (globalThis.navigator.sendBeacon(endpoint, blob)) {
                return
            }
        }
    } catch (e) {
        // Fall through to fetch.
    }

    try {
        globalThis.fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body,
            // The page is unloading; without this the request dies with the document.
            keepalive: true,
            // Never send cookies: the endpoint is unauthenticated by design and the payload
            // carries no user identity, so a session would add a CSRF surface for no gain.
            credentials: 'omit',
        }).catch(() => {
            // Silent: a dropped measurement is not worth a console message on every
            // offline or ad-blocked page load.
        })
    } catch (e) {
        // fetch can throw synchronously on a malformed request.
    }
}

function payload(state) {
    const navigation = navigationEntry()

    return {
        token: state.config.token,
        view_id: state.viewId,
        // Path only. The query string is where identifying data lives (a magic-link token, an
        // email in a callback), and the server resolves this to a route pattern and discards
        // the path — so the value is never stored either.
        path: pagePath(),
        trace_id: state.traceId,
        nav_type: navigationType(navigation),
        redirect_count: integer(navigation?.redirectCount),
        connection: connectionClass(),
        // One immutable timestamp identifies the page view across corrective re-flushes;
        // changing it would move the same view between already-closed rollup windows.
        occurred_at: state.occurredAt,
        metrics: metrics(state, navigation),
        cls: round(state.cls, 4),
        ...resources(),
        round_trip_count: state.roundTripCount,
        round_trip_ms_total: state.roundTripMsTotal,
        round_trips: state.roundTrips,
    }
}

function pagePath() {
    try {
        return globalThis.location.pathname || '/'
    } catch (e) {
        return '/'
    }
}

function navigationType(navigation) {
    const type = navigation?.type

    // The API spells it back_forward; the column stores the same vocabulary.
    return typeof type === 'string' && type !== '' ? type : null
}

function connectionClass() {
    try {
        const effectiveType = globalThis.navigator?.connection?.effectiveType

        return typeof effectiveType === 'string' && effectiveType !== '' ? effectiveType : 'unknown'
    } catch (e) {
        return 'unknown'
    }
}

/**
 * Vitals and the navigation breakdown, in one flat object keyed by column name.
 *
 * A metric the browser never reported is *omitted* rather than sent as 0 — the server stores
 * null for it, because "no interaction happened" and "the interaction was instant" are
 * different facts and only one of them is true.
 */
function metrics(state, navigation) {
    const metrics = {}

    set(metrics, 'lcp_ms', state.lcp)
    set(metrics, 'inp_ms', state.inp)
    set(metrics, 'fcp_ms', paintTime('first-contentful-paint'))

    if (!navigation) {
        return metrics
    }

    // TTFB is measured from the navigation's own start, so a redirect chain is included —
    // which is correct: the user waited for it.
    set(metrics, 'ttfb_ms', navigation.responseStart)
    set(metrics, 'dns_ms', delta(navigation.domainLookupEnd, navigation.domainLookupStart))
    set(metrics, 'tcp_ms', delta(navigation.connectEnd, navigation.connectStart))
    set(
        metrics,
        'tls_ms',
        navigation.secureConnectionStart > 0
            ? delta(navigation.connectEnd, navigation.secureConnectionStart)
            : null,
    )
    set(metrics, 'request_ms', delta(navigation.responseStart, navigation.requestStart))
    set(metrics, 'response_ms', delta(navigation.responseEnd, navigation.responseStart))
    set(metrics, 'dom_interactive_ms', navigation.domInteractive)
    set(metrics, 'dom_content_loaded_ms', navigation.domContentLoadedEventEnd)
    // Zero until the load event has actually fired, and a page hidden before load never
    // fires it — null is the honest answer there.
    set(metrics, 'load_event_ms', navigation.loadEventEnd > 0 ? navigation.loadEventEnd : null)

    return metrics
}

function paintTime(name) {
    try {
        const entries = globalThis.performance?.getEntriesByType?.('paint') ?? []

        for (const entry of entries) {
            if (entry.name === name) {
                return entry.startTime
            }
        }
    } catch (e) {
        // No paint timing on this browser.
    }

    return null
}

/**
 * Per-initiator-type asset summary, read once at flush rather than observed: the entries are
 * already in the browser's buffer, and an observer would pay a callback per request on a page
 * that loads two hundred of them.
 */
function resources() {
    const summary = {}
    let count = 0
    let transferBytes = 0

    try {
        const entries = globalThis.performance?.getEntriesByType?.('resource') ?? []

        for (const entry of entries) {
            const kind = resourceKind(entry.initiatorType)
            const bucket = summary[kind] ?? (summary[kind] = { count: 0, transfer_bytes: 0, duration_ms: 0 })
            // transferSize is 0 for a cross-origin response without Timing-Allow-Origin, and
            // for a cache hit. Both are honest zeros: no bytes crossed the network.
            const bytes = integer(entry.transferSize)

            bucket.count++
            bucket.transfer_bytes += bytes
            bucket.duration_ms += integer(entry.duration)

            count++
            transferBytes += bytes
        }
    } catch (e) {
        // No resource timing on this browser.
    }

    return { resource_count: count, transfer_bytes: transferBytes, resources: summary }
}

function resourceKind(initiatorType) {
    if (initiatorType === 'img') {
        return 'image'
    }

    if (initiatorType === 'xmlhttprequest') {
        return 'fetch'
    }

    if (initiatorType === 'link') {
        return 'css'
    }

    return RESOURCE_KINDS.includes(initiatorType) ? initiatorType : 'other'
}

function set(target, key, value) {
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
        target[key] = Math.round(value)
    }
}

function delta(end, start) {
    if (typeof end !== 'number' || typeof start !== 'number' || end <= 0 || start <= 0) {
        return null
    }

    return Math.max(0, end - start)
}

function integer(value) {
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : 0
}

function round(value, places) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return 0
    }

    const factor = 10 ** places

    return Math.round(value * factor) / factor
}

function now() {
    try {
        return globalThis.performance?.now?.() ?? Date.now()
    } catch (e) {
        return Date.now()
    }
}

/** Test seam: the module keeps install-once state that a fresh import would otherwise carry. */
export function resetPerformanceCollector() {
    installed = false
}
